export function createConversationHandlers({
    selectedFolder,
    totalPages,
    graphStatus,
    mailForm,
    selectedConversation,
    setSearchQuery,
    setPage,
    setSelectedFolder,
    setIsComposeMode,
    setSortBy,
    setConnectingGraph,
    setGraphStatusError,
    setDisconnectingGraph,
    setMailFeedback,
    setMessagesError,
    setSelectedConversationId,
    setSendingMail,
    setMailForm,
    getGraphConnectURL,
    disconnectGraph,
    refreshGraphStatus,
    sendGraphMail,
}) {
    function ensureSubjectPrefix(subject, prefix) {
        const normalizedSubject = String(subject || "").trim() || "(no subject)";
        const prefixLower = prefix.toLowerCase();
        if (normalizedSubject.toLowerCase().startsWith(prefixLower)) {
            return normalizedSubject;
        }
        return `${prefix}${normalizedSubject}`;
    }

    function buildForwardBody(message) {
        const sentAt = message?.sent_at || "";
        const direction = message?.direction || "message";
        const originalBody = String(message?.body_text || "").trim() || "(no body)";
        return `\n\n---------- Forwarded message ----------\nDirection: ${direction}\nSent: ${sentAt}\n\n${originalBody}`;
    }

    function handleFolderClick(folderId) {
        if (folderId !== selectedFolder) {
            setSearchQuery("");
            setPage(1);
        }
        setSelectedFolder(folderId);
        setIsComposeMode(false);
    }

    function handleSearchQueryChange(value) {
        setSearchQuery(value);
        setPage(1);
    }

    function handleSortChange(value) {
        setSortBy(value);
        setPage(1);
    }

    function handlePrevPage() {
        setPage((prev) => Math.max(1, prev - 1));
    }

    function handleNextPage() {
        setPage((prev) => Math.min(Math.max(totalPages, 1), prev + 1));
    }

    async function handleConnectGraph() {
        setConnectingGraph(true);
        setGraphStatusError("");
        try {
            const url = await getGraphConnectURL();
            if (!url) {
                throw new Error("Microsoft connect URL is empty");
            }
            window.location.href = url;
        } catch (err) {
            setGraphStatusError(err?.message || "Failed to start Microsoft connection");
            setConnectingGraph(false);
        }
    }

    async function handleDisconnectGraph() {
        setDisconnectingGraph(true);
        setGraphStatusError("");
        try {
            await disconnectGraph();
            await refreshGraphStatus();
        } catch (err) {
            setGraphStatusError(err?.message || "Failed to disconnect Microsoft account");
        } finally {
            setDisconnectingGraph(false);
        }
    }

    function handleComposeFormChange(field, value) {
        setMailForm((prev) => ({ ...prev, [field]: value }));
    }

    function handleStartCompose() {
        if (!graphStatus?.connected) {
            setMailFeedback("Connect Microsoft 365 before sending email.");
            return;
        }

        setMessagesError("");
        setMailFeedback("");
        setSelectedConversationId(null);
        setIsComposeMode(true);
    }

    function handleReplyFromMessage(message) {
        if (!graphStatus?.connected) {
            setMailFeedback("Connect Microsoft 365 before sending email.");
            return;
        }

        const recipient = String(message?.from_email || selectedConversation?.contact_email || "").trim();
        const subject = ensureSubjectPrefix(message?.subject, "Re: ");
        const body = `\n\nOn ${message?.sent_at || "(unknown time)"}, ${selectedConversation?.contact_name || "contact"} wrote:\n${String(message?.body_text || "").trim() || "(no body)"}`;

        setMessagesError("");
        setMailFeedback(recipient ? "" : "Reply recipient not found for this conversation. Enter an email manually.");
        setMailForm({
            to: recipient,
            subject,
            body,
        });
        setIsComposeMode(true);
    }

    function handleReplyAllFromMessage(message) {
        if (!graphStatus?.connected) {
            setMailFeedback("Connect Microsoft 365 before sending email.");
            return;
        }

        const recipientSet = new Set();
        const fromEmail = String(message?.from_email || "").trim();
        const toEmail = String(message?.to_email || "").trim();
        const fallbackEmail = String(selectedConversation?.contact_email || "").trim();
        if (fromEmail) {
            recipientSet.add(fromEmail);
        }
        if (toEmail) {
            recipientSet.add(toEmail);
        }
        if (!recipientSet.size && fallbackEmail) {
            recipientSet.add(fallbackEmail);
        }
        const recipients = Array.from(recipientSet).join(", ");
        const subject = ensureSubjectPrefix(message?.subject, "Re: ");
        const body = `\n\nOn ${message?.sent_at || "(unknown time)"}, ${selectedConversation?.contact_name || "contact"} wrote:\n${String(message?.body_text || "").trim() || "(no body)"}`;

        setMessagesError("");
        setMailFeedback(
            recipients
                ? "Reply All uses available conversation recipients."
                : "Reply All recipients not found for this conversation. Enter emails manually.",
        );
        setMailForm({
            to: recipients,
            subject,
            body,
        });
        setIsComposeMode(true);
    }

    function handleForwardFromMessage(message) {
        if (!graphStatus?.connected) {
            setMailFeedback("Connect Microsoft 365 before sending email.");
            return;
        }

        setMessagesError("");
        setMailFeedback("");
        setMailForm({
            to: "",
            subject: ensureSubjectPrefix(message?.subject, "Fwd: "),
            body: buildForwardBody(message),
        });
        setIsComposeMode(true);
    }

    function handleCancelCompose() {
        setIsComposeMode(false);
        setMailFeedback("");
    }

    async function handleSendMail() {
        setMailFeedback("");
        if (!mailForm.to.trim() || !mailForm.subject.trim()) {
            setMailFeedback("To and Subject are required.");
            return;
        }

        setSendingMail(true);
        try {
            await sendGraphMail({
                to: mailForm.to.trim(),
                subject: mailForm.subject.trim(),
                body: mailForm.body || "",
            });
            setMailFeedback("Email sent.");
            setMailForm((prev) => ({ ...prev, subject: "", body: "" }));
            setIsComposeMode(false);
        } catch (err) {
            setMailFeedback(err?.message || "Failed to send email.");
        } finally {
            setSendingMail(false);
        }
    }

    return {
        handleFolderClick,
        handleSearchQueryChange,
        handleSortChange,
        handlePrevPage,
        handleNextPage,
        handleConnectGraph,
        handleDisconnectGraph,
        handleComposeFormChange,
        handleStartCompose,
        handleReplyFromMessage,
        handleReplyAllFromMessage,
        handleForwardFromMessage,
        handleCancelCompose,
        handleSendMail,
    };
}
